import React, { useState, useCallback } from "react";
import Select from "react-select";
import { LocationData } from "../types";
import { LocationMap } from "../store/slices/locationsSlice";

interface LocationSelectProps {
  value: string | LocationData[];
  onChange: (
    location: string | LocationData[],
    locationData?: LocationData
  ) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  isMulti?: boolean;
  existingLocations: LocationMap;
}

// Utility function to generate location ID
const generateLocationId = (text: string): string => {
  const snakeCase = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_");
  const randomNum = Math.floor(Math.random() * 10000);
  return `location_${snakeCase}_${randomNum}`;
};

// Utility function to extract country code from address components
const extractCountryCode = (addressComponents: any[]): string | undefined => {
  const countryComponent = addressComponents?.find((component: any) =>
    component.types.includes("country")
  );
  return countryComponent?.short_name;
};

export const LocationSelect: React.FC<LocationSelectProps> = ({
  value,
  onChange,
  placeholder = "Search for a location...",
  className = "",
  error = false,
  isMulti = false,
  existingLocations,
}) => {
  const [locationOptions, setLocationOptions] = useState<
    Array<{ value: string; label: string; locationData: LocationData }>
  >([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  // Google Places search function
  const searchLocations = useCallback(
    async (inputValue: string) => {
      if (!inputValue || inputValue.length < 3) {
        setLocationOptions([]);
        return;
      }

      if (!googleMapsLoaded || !window.google) {
        console.warn("Google Maps API not yet loaded");
        return;
      }

      setLocationLoading(true);
      try {
        const service = new window.google.maps.places.AutocompleteService();
        const request = {
          input: inputValue,
          types: ["establishment", "geocode"],
        };

        service.getPlacePredictions(
          request,
          (predictions: any, status: any) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              const options = predictions.slice(0, 5).map((prediction: any) => {
                const locationData: LocationData = {
                  id: generateLocationId(prediction.description),
                  placeId: prediction.place_id,
                  placeName: prediction.description,
                  shortLabel:
                    prediction.structured_formatting?.main_text ||
                    prediction.description,
                  lat: 0, // Will be updated when place details are fetched
                  lng: 0, // Will be updated when place details are fetched
                  types: prediction.types,
                };

                return {
                  value: prediction.description,
                  label: prediction.description,
                  locationData,
                };
              });
              setLocationOptions(options);
            } else {
              setLocationOptions([]);
            }
            setLocationLoading(false);
          }
        );
      } catch (error) {
        console.error("Error fetching locations:", error);
        setLocationOptions([]);
        setLocationLoading(false);
      }
    },
    [googleMapsLoaded]
  );

  const handleLocationInputChange = (inputValue: string) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = window.setTimeout(() => {
      searchLocations(inputValue);
    }, 300); // 300ms debounce

    setDebounceTimer(timer);
  };

  // Function to get place details when a location is selected
  const getPlaceDetails = useCallback(
    (
      placeIds: string[],
      locationDataArray: LocationData[],
      callback: (updatedData: LocationData[]) => void
    ) => {
      if (!googleMapsLoaded || !window.google) {
        console.warn("Google Maps API not yet loaded");
        callback(locationDataArray); // Return original data without coordinates
        return;
      }

      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      const updatedLocations: LocationData[] = [];
      let completedRequests = 0;

      placeIds.forEach((placeId, index) => {
        const request = {
          placeId: placeId,
          fields: ["geometry", "address_components"],
        };

        service.getDetails(request, (place: any, status: any) => {
          const locationData: LocationData = locationDataArray[index];

          // Check if this location already exists
          const existingLocation: LocationData | undefined =
            existingLocations[placeId];

          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            updatedLocations[index] = {
              ...locationData,
              id: existingLocation?.id || locationData.id,
              placeId: placeId,
              lat: place.geometry?.location?.lat() || 0,
              lng: place.geometry?.location?.lng() || 0,
              countryCode: place.address_components
                ? extractCountryCode(place.address_components)
                : undefined,
              existingLocation: !!existingLocation,
            };
          } else {
            console.warn(`Failed to get details for place ${placeId}:`, status);
            updatedLocations[index] = {
              ...locationData,
              id: existingLocation?.id || locationData.id,
              placeId: placeId,
              existingLocation: !!existingLocation,
            }; // Keep original data if details fail
          }

          completedRequests++;
          if (completedRequests === placeIds.length) {
            callback(updatedLocations);
          }
        });
      });
    },
    [googleMapsLoaded, existingLocations]
  );

  const handleLocationChange = (selectedOption: any) => {
    if (isMulti) {
      // Handle multi-select
      if (selectedOption && Array.isArray(selectedOption)) {
        const locationDataArray = selectedOption.map(
          (option) => option.locationData
        );
        const placeIds = locationDataArray.map((data) => data.placeId);

        setLocationLoading(true);
        getPlaceDetails(placeIds, locationDataArray, (updatedLocations) => {
          setLocationLoading(false);
          onChange(updatedLocations);
        });
      } else {
        onChange([]);
      }
    } else {
      // Handle single select
      if (selectedOption) {
        const locationData = selectedOption.locationData;
        setLocationLoading(true);
        getPlaceDetails(
          [locationData.placeId],
          [locationData],
          (updatedLocations) => {
            setLocationLoading(false);
            if (updatedLocations.length > 0) {
              onChange(updatedLocations[0].placeName, updatedLocations[0]);
            } else {
              // Fallback if details fetch fails
              onChange(locationData.placeName, locationData);
            }
          }
        );
      } else {
        onChange("");
      }
    }
  };

  const getSelectValue = () => {
    if (isMulti && Array.isArray(value)) {
      return value.map((loc) => ({
        value: loc.placeName,
        label: loc.placeName,
        locationData: loc,
      }));
    } else if (!isMulti && typeof value === "string") {
      return value ? { value, label: value } : null;
    }
    return isMulti ? [] : null;
  };

  // Load Google Places API script if not already loaded
  React.useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setGoogleMapsLoaded(true);
      return;
    }

    if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_PLACES_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setGoogleMapsLoaded(true);
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API");
      };
      document.head.appendChild(script);
    }
  }, []);

  return (
    <Select
      value={getSelectValue()}
      isMulti={isMulti}
      onInputChange={handleLocationInputChange}
      onChange={handleLocationChange}
      options={locationOptions}
      isLoading={locationLoading}
      placeholder={placeholder}
      className={`connections-select ${className} ${error ? "error" : ""}`}
      classNamePrefix="select"
      isClearable
      isSearchable
      noOptionsMessage={({ inputValue }) =>
        inputValue.length < 3
          ? "Type at least 3 characters to search"
          : "No places found"
      }
    />
  );
};
